/**
 * Wishlist Modal Component
 * Displays user's private wishlist organized by category
 * Categories: Tracks, Albums/EPs, Courses, Events, Gear, Venues
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, Star, Music, Calendar, MapPin, Package, Trash2, ExternalLink, Loader2, Filter, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WishlistModal = ({ isOpen, onClose, onViewTrack }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [wishlistData, setWishlistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingItem, setRemovingItem] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle responsive state
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const tabs = [
    { id: 'all', label: 'All Items', icon: '⭐', count: 0 },
    { id: 'music', label: 'Music', icon: '🎵', count: 0 },
    { id: 'courses', label: 'Courses', icon: '🎓', count: 0 },
    { id: 'events', label: 'Events', icon: '🎫', count: 0 },
    { id: 'gear', label: 'Gear', icon: '🎛️', count: 0 },
    { id: 'merch', label: 'Merch', icon: '👕', count: 0 },
    { id: 'venues', label: 'Venues', icon: '📍', count: 0 },
  ];

  const fetchWishlist = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('beatseek_token');
      
      if (!token) {
        setError('Please login to view your wishlist');
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/wishlist`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch wishlist');
      }

      const data = await response.json();
      setWishlistData(data);
    } catch (err) {
      console.error('Error fetching wishlist:', err);
      setError('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchWishlist();
    }
  }, [isOpen, fetchWishlist]);

  const handleRemoveItem = async (itemId) => {
    try {
      setRemovingItem(itemId);
      const token = localStorage.getItem('beatseek_token');

      const response = await fetch(`${BACKEND_URL}/api/wishlist/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to remove item');
      }

      // Refresh wishlist
      fetchWishlist();
    } catch (err) {
      console.error('Error removing item:', err);
      alert('Failed to remove item from wishlist');
    } finally {
      setRemovingItem(null);
    }
  };

  const getDisplayItems = () => {
    if (!wishlistData) return [];
    
    if (activeTab === 'all') {
      return wishlistData.items.all || [];
    }
    
    // Combine tracks and albums into "music" tab
    if (activeTab === 'music') {
      const tracks = wishlistData.items.tracks || [];
      const albums = wishlistData.items.albums || [];
      return [...tracks, ...albums].sort((a, b) => 
        new Date(b.added_at) - new Date(a.added_at)
      );
    }
    
    return wishlistData.items[activeTab] || [];
  };

  const getUpdatedTabs = () => {
    if (!wishlistData) return tabs;
    
    return tabs.map(tab => {
      if (tab.id === 'all') {
        return { ...tab, count: wishlistData.total_count };
      }
      // Combine tracks and albums count for "music" tab
      if (tab.id === 'music') {
        const tracksCount = wishlistData.counts.tracks || 0;
        const albumsCount = wishlistData.counts.albums || 0;
        return { ...tab, count: tracksCount + albumsCount };
      }
      return { ...tab, count: wishlistData.counts[tab.id] || 0 };
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const renderWishlistItem = (item) => {
    const itemData = item.item_data || {};
    
    return (
      <div 
        key={item.id}
        className="wishlist-item-card"
        data-testid={`wishlist-item-${item.id}`}
        style={{
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.9))',
          border: '1px solid rgba(255, 215, 0, 0.3)',
          borderRadius: isMobile ? '10px' : '12px',
          padding: isMobile ? '0.75rem' : '1rem',
          marginBottom: isMobile ? '0.5rem' : '0.75rem',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? '0.75rem' : '1rem',
          transition: 'all 0.2s ease'
        }}
      >
        {/* Top row on mobile: Icon + Details */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? '0.75rem' : '1rem',
          flex: 1,
          minWidth: 0
        }}>
          {/* Item Type Icon */}
          <div 
            className="wishlist-item-icon"
            style={{
              width: isMobile ? '36px' : '50px',
              height: isMobile ? '36px' : '50px',
              borderRadius: isMobile ? '8px' : '10px',
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.2), rgba(255, 165, 0, 0.2))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isMobile ? '1.1rem' : '1.5rem',
              flexShrink: 0
            }}
          >
            {item.item_type === 'track' && '🎵'}
            {item.item_type === 'album' && '💿'}
            {item.item_type === 'course' && '🎓'}
            {item.item_type === 'event' && '🎫'}
            {item.item_type === 'gear' && '🎛️'}
            {item.item_type === 'merch' && '👕'}
            {item.item_type === 'venue' && '📍'}
          </div>

          {/* Item Details */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h4 
              className="wishlist-item-title"
              style={{ 
                margin: '0 0 0.25rem 0', 
                color: '#fff', 
                fontSize: isMobile ? '0.85rem' : '1rem',
                fontWeight: '600',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {itemData.name || itemData.title || 'Unknown Item'}
            </h4>
            
            <div 
              className="wishlist-item-meta"
              style={{ 
                color: 'rgba(255, 255, 255, 0.6)', 
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                flexWrap: 'wrap'
              }}
            >
              {/* Track/Album specific */}
              {(item.item_type === 'track' || item.item_type === 'album') && itemData.artist && (
                <span>by {itemData.artist}</span>
              )}
              
              {/* Course specific */}
              {item.item_type === 'course' && (
                <>
                  {itemData.artist && <span>by {itemData.artist}</span>}
                  {itemData.course_type && <span>• {itemData.course_type}</span>}
                </>
              )}
              
              {/* Event specific */}
              {item.item_type === 'event' && (
                <>
                  {itemData.date && <span>📅 {formatDate(itemData.date)}</span>}
                  {itemData.location && <span>📍 {itemData.location}</span>}
                </>
              )}
              
              {/* Gear specific */}
              {item.item_type === 'gear' && (
                <>
                  {itemData.price && <span style={{ color: '#00ff00' }}>${itemData.price}</span>}
                  {itemData.condition && <span>• {itemData.condition}</span>}
                </>
              )}
              
              {/* Merch specific */}
              {item.item_type === 'merch' && (
                <>
                  {itemData.price && <span style={{ color: '#00ff00' }}>${itemData.price}</span>}
                  {itemData.seller_name && <span>• by {itemData.seller_name}</span>}
                  {itemData.product_type && <span>• {itemData.product_type}</span>}
                </>
              )}
              
              {/* Venue specific */}
              {item.item_type === 'venue' && itemData.location && (
                <span>📍 {itemData.location}</span>
              )}
            </div>

            {/* Notes */}
            {item.notes && (
              <p 
                className="wishlist-item-notes"
                style={{ 
                  margin: '0.5rem 0 0 0', 
                  color: 'rgba(255, 215, 0, 0.7)', 
                  fontSize: isMobile ? '0.65rem' : '0.8rem',
                  fontStyle: 'italic'
                }}
              >
                &quot;{item.notes}&quot;
              </p>
            )}

            {/* Added date */}
            <span 
              className="wishlist-item-date"
              style={{ 
                color: 'rgba(255, 255, 255, 0.4)', 
                fontSize: isMobile ? '0.6rem' : '0.75rem',
                display: 'block',
                marginTop: '0.25rem'
              }}
            >
              Added {formatDate(item.added_at)}
            </span>
          </div>
        </div>

        {/* Actions - Bottom row on mobile */}
        <div 
          className="wishlist-item-actions"
          style={{ 
            display: 'flex', 
            gap: '0.5rem', 
            flexShrink: 0, 
            alignItems: 'center',
            justifyContent: isMobile ? 'flex-end' : 'flex-start'
          }}
        >
          {/* Track-specific: Navigate to Discovery Zone */}
          {item.item_type === 'track' && (
            <button
              className="wishlist-action-btn"
              data-testid={`wishlist-view-${item.id}`}
              onClick={() => {
                if (onViewTrack) {
                  onViewTrack(itemData);
                  onClose();
                }
              }}
              style={{
                padding: isMobile ? '0.35rem 0.6rem' : '0.5rem 1rem',
                background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.2), rgba(255, 0, 255, 0.2))',
                border: '1px solid rgba(0, 255, 255, 0.4)',
                borderRadius: isMobile ? '6px' : '8px',
                color: '#00ffff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                fontSize: isMobile ? '0.65rem' : '0.8rem',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(0, 255, 255, 0.3), rgba(255, 0, 255, 0.3))';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(0, 255, 255, 0.2), rgba(255, 0, 255, 0.2))';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <Search size={isMobile ? 12 : 14} />
              {isMobile ? 'View' : 'View Track'}
            </button>
          )}

          {/* External link button for non-track items */}
          {item.item_type !== 'track' && (itemData.url || itemData.external_link || itemData.ticket_url) && (
            <button
              className="wishlist-action-btn"
              data-testid={`wishlist-external-${item.id}`}
              onClick={() => {
                const url = itemData.url || itemData.external_link || itemData.ticket_url;
                if (url) {
                  window.open(url, '_blank');
                }
              }}
              style={{
                padding: isMobile ? '0.35rem 0.6rem' : '0.5rem 1rem',
                background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.2), rgba(255, 0, 255, 0.2))',
                border: '1px solid rgba(0, 255, 255, 0.4)',
                borderRadius: isMobile ? '6px' : '8px',
                color: '#00ffff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                fontSize: isMobile ? '0.65rem' : '0.8rem',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(0, 255, 255, 0.3), rgba(255, 0, 255, 0.3))';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(0, 255, 255, 0.2), rgba(255, 0, 255, 0.2))';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <ExternalLink size={isMobile ? 12 : 14} />
              {isMobile ? 'View' : (
                <>
                  {item.item_type === 'album' && 'View Album'}
                  {item.item_type === 'course' && 'View Course'}
                  {item.item_type === 'event' && 'View Event'}
                  {item.item_type === 'gear' && 'View Listing'}
                  {item.item_type === 'merch' && 'View Merch'}
                  {item.item_type === 'venue' && 'View Venue'}
                </>
              )}
            </button>
          )}

          {/* Remove Button */}
          <button
            className="wishlist-remove-btn"
            data-testid={`wishlist-remove-${item.id}`}
            onClick={() => handleRemoveItem(item.id)}
            disabled={removingItem === item.id}
            style={{
              padding: isMobile ? '0.35rem 0.5rem' : '0.5rem 0.75rem',
              background: 'rgba(255, 0, 0, 0.1)',
              border: '1px solid rgba(255, 0, 0, 0.3)',
              borderRadius: isMobile ? '6px' : '8px',
              color: '#ff6b6b',
              cursor: removingItem === item.id ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: removingItem === item.id ? 0.5 : 1
            }}
            title="Remove from Wishlist"
          >
            {removingItem === item.id ? <Loader2 size={isMobile ? 14 : 16} className="animate-spin" /> : <Trash2 size={isMobile ? 14 : 16} />}
          </button>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  const displayItems = getDisplayItems();
  const updatedTabs = getUpdatedTabs();

  return (
    <div 
      className="wishlist-modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'center',
        zIndex: 99999,
        padding: isMobile ? '0.5rem' : '1rem',
        paddingTop: isMobile ? '0.5rem' : '1rem',
        paddingBottom: isMobile ? '100px' : '1rem'
      }}
      onClick={onClose}
    >
      <div 
        className="wishlist-modal-container"
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
          borderRadius: isMobile ? '16px' : '20px',
          width: '100%',
          maxWidth: isMobile ? '100%' : '1000px',
          minHeight: isMobile ? '60vh' : '50vh',
          maxHeight: isMobile ? 'calc(100vh - 120px)' : '85vh',
          overflow: 'hidden',
          border: '2px solid rgba(255, 215, 0, 0.4)',
          boxShadow: '0 0 60px rgba(255, 215, 0, 0.2)',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="wishlist-modal-header"
          style={{
            padding: isMobile ? '1rem' : '1.5rem',
            borderBottom: '1px solid rgba(255, 215, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0.5rem' : '0.75rem' }}>
            <span style={{ fontSize: isMobile ? '1.5rem' : '2rem' }}>⭐</span>
            <div>
              <h2 style={{ 
                margin: 0, 
                fontSize: isMobile ? '1.1rem' : '1.5rem',
                background: 'linear-gradient(135deg, #ffd700 0%, #ffa500 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                My Wishlist
              </h2>
              {!isMobile && (
                <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem' }}>
                  Gear &amp; items you want
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="wishlist-close-btn"
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              borderRadius: '50%',
              width: isMobile ? '32px' : '40px',
              height: isMobile ? '32px' : '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#fff'
            }}
          >
            <X size={isMobile ? 18 : 20} />
          </button>
        </div>

        {/* Tabs */}
        <div 
          className="wishlist-tabs-container wishlist-tabs-scroll"
          style={{
            padding: isMobile ? '0.75rem 1rem' : '1rem 1.5rem',
            borderBottom: '1px solid rgba(255, 215, 0, 0.1)',
            display: 'flex',
            gap: isMobile ? '0.4rem' : '0.5rem',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {updatedTabs.map(tab => (
            <button
              key={tab.id}
              className="wishlist-tab-btn"
              onClick={() => setActiveTab(tab.id)}
              data-testid={`wishlist-tab-${tab.id}`}
              style={{
                padding: isMobile ? '0.4rem 0.75rem' : '0.5rem 1rem',
                background: activeTab === tab.id 
                  ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.3), rgba(255, 165, 0, 0.3))'
                  : 'rgba(255, 255, 255, 0.05)',
                border: activeTab === tab.id 
                  ? '1px solid rgba(255, 215, 0, 0.5)'
                  : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '20px',
                color: activeTab === tab.id ? '#ffd700' : 'rgba(255, 255, 255, 0.7)',
                cursor: 'pointer',
                fontSize: isMobile ? '0.75rem' : '0.85rem',
                fontWeight: activeTab === tab.id ? '600' : '400',
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? '0.3rem' : '0.5rem',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease',
                flexShrink: 0
              }}
            >
              <span>{tab.icon}</span>
              <span>{isMobile && tab.id !== 'all' ? '' : tab.label}</span>
              {tab.count > 0 && (
                <span 
                  className="tab-count"
                  style={{
                    background: 'rgba(255, 215, 0, 0.3)',
                    padding: '0.1rem 0.4rem',
                    borderRadius: '10px',
                    fontSize: isMobile ? '0.65rem' : '0.75rem',
                    fontWeight: '600'
                  }}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div 
          className="wishlist-content-area"
          style={{
            padding: isMobile ? '1rem' : '1.5rem',
            flex: 1,
            overflowY: 'auto',
            minHeight: 0
          }}
        >
          {loading ? (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center', 
              padding: isMobile ? '2rem' : '3rem',
              color: 'rgba(255, 255, 255, 0.6)'
            }}>
              <Loader2 size={isMobile ? 30 : 40} className="animate-spin" style={{ marginBottom: '1rem' }} />
              <span style={{ fontSize: isMobile ? '0.85rem' : '1rem' }}>Loading your wishlist...</span>
            </div>
          ) : error ? (
            <div style={{ 
              textAlign: 'center', 
              padding: isMobile ? '2rem' : '3rem',
              color: '#ff6b6b'
            }}>
              <p style={{ fontSize: isMobile ? '0.85rem' : '1rem' }}>{error}</p>
            </div>
          ) : displayItems.length === 0 ? (
            <div 
              className="wishlist-empty-state"
              style={{ 
                textAlign: 'center', 
                padding: isMobile ? '2rem 1rem' : '3rem',
                color: 'rgba(255, 255, 255, 0.6)'
              }}
            >
              <span style={{ fontSize: isMobile ? '2.5rem' : '3rem', display: 'block', marginBottom: '1rem' }}>⭐</span>
              <h3 style={{ color: '#fff', marginBottom: '0.5rem', fontSize: isMobile ? '1rem' : '1.2rem' }}>
                {activeTab === 'all' ? 'Your wishlist is empty' : `No ${activeTab} in your wishlist`}
              </h3>
              <p style={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                Start adding items by clicking the ⭐ icon on tracks, events, or gear!
              </p>
            </div>
          ) : (
            <div>
              {displayItems.map(item => renderWishlistItem(item))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Export a helper function to add items to wishlist from anywhere
export const addToWishlist = async (itemType, itemData, notes = null) => {
  try {
    const token = localStorage.getItem('beatseek_token');
    
    if (!token) {
      alert('Please login to add items to your wishlist');
      return false;
    }

    const response = await fetch(`${BACKEND_URL}/api/wishlist`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        item_type: itemType,
        item_data: itemData,
        notes: notes
      })
    });

    if (response.status === 400) {
      alert('⭐ Item is already in your wishlist!');
      return false;
    }

    if (!response.ok) {
      throw new Error('Failed to add to wishlist');
    }

    alert('⭐ Added to wishlist!');
    return true;
  } catch (err) {
    console.error('Error adding to wishlist:', err);
    alert('Failed to add to wishlist. Please try again.');
    return false;
  }
};

// Export a helper to check if item is in wishlist
export const checkWishlistStatus = async (itemType, itemId) => {
  try {
    const token = localStorage.getItem('beatseek_token');
    if (!token) return { in_wishlist: false };

    const response = await fetch(`${BACKEND_URL}/api/wishlist/check/${itemType}/${itemId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) return { in_wishlist: false };
    return await response.json();
  } catch (err) {
    return { in_wishlist: false };
  }
};

// Export a helper to remove from wishlist
export const removeFromWishlist = async (itemId) => {
  try {
    const token = localStorage.getItem('beatseek_token');
    if (!token) return false;

    const response = await fetch(`${BACKEND_URL}/api/wishlist/${itemId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return response.ok;
  } catch (err) {
    return false;
  }
};

export default WishlistModal;
